import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Typography } from '@mui/material';
import { useAuth0 } from '@auth0/auth0-react';

import { FHInputField, FHSelect } from '../../../base';
import { clientFamilyBackgroundActions } from '../../../../store';
import { useTreatmentPlanEditable } from '../hooks/useTreatmentPlanEditable';
import { capitalizeFirstLetter } from '../../../../helpers';
import { FORTA_ERROR, FORTA_GRAY3, FORTA_BLUE } from '../../../../utils/colors';
import { Themes } from '../../../../constants';
import { MemberLanguages } from './constants';
import ErrorIcon from '../../../../assets/images/error-alert.svg';

import type { SelectValueType } from '../../../../utils/types';
import type { RootState } from '../../../../store';

function MemberLanguage() {
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();
  const isEditable = useTreatmentPlanEditable();
  const { current: treatmentPlan } = useSelector((state: RootState) => state.treatmentPlans);
  const {
    clientFamilyBackground,
    originalClientFamilyBackground,
    parentCaregiverErrorCounts,
    siblingErrorCounts,
    memberlanguageErrorCount,
    strengthErrorCount,
    interestErrorCount,
    familyBackgroundErrorCount,
  } = useSelector((state: RootState) => state.clientFamilyBackgrounds);

  const [language, setLanguage] = useState<MemberLanguages>(MemberLanguages.English);
  const [otherLanguage, setOtherLanguage] = useState<string>('');
  const [
    clientAndFamilyBackgroundSectionErrorCount,
    setClientAndFamilyBackgroundSectionErrorCount,
  ] = useState<number>(0);

  const isOtherLanguageValid = useMemo(() => {
    return otherLanguage?.length >= 5;
  }, [otherLanguage]);

  useEffect(() => {
    const hasError =
      (isEditable && language === '') ||
      (language === MemberLanguages.Other && !isOtherLanguageValid);
    dispatch(
      clientFamilyBackgroundActions.updateState({
        key: 'memberlanguageErrorCount',
        data: hasError ? 1 : 0,
      }),
    );
  }, [dispatch, isEditable, language, isOtherLanguageValid]);

  useEffect(() => {
    if (!treatmentPlan || !clientFamilyBackground) return;
    const memberLanguage = clientFamilyBackground.member_language;

    if (!memberLanguage) {
      setLanguage(MemberLanguages.Null);
      setOtherLanguage('');
    } else {
      const predefinedLanguage = Object.values(MemberLanguages).find(
        (lang) => lang === memberLanguage,
      );

      if (predefinedLanguage) {
        setLanguage(predefinedLanguage);
        setOtherLanguage('');
        return;
      }

      setLanguage(MemberLanguages.Other);
      setOtherLanguage(memberLanguage);
    }
  }, [clientFamilyBackground, treatmentPlan]);

  useEffect(() => {
    let errors = 0;
    if (parentCaregiverErrorCounts) errors = +parentCaregiverErrorCounts;
    if (siblingErrorCounts) errors = +siblingErrorCounts;
    if (memberlanguageErrorCount) errors = +memberlanguageErrorCount;
    if (strengthErrorCount) errors = +strengthErrorCount;
    if (interestErrorCount) errors = +interestErrorCount;
    if (familyBackgroundErrorCount) errors = +familyBackgroundErrorCount;
    setClientAndFamilyBackgroundSectionErrorCount(errors);
  }, [
    parentCaregiverErrorCounts,
    siblingErrorCounts,
    memberlanguageErrorCount,
    strengthErrorCount,
    interestErrorCount,
    familyBackgroundErrorCount,
  ]);

  const handleSaveLanguage = (memberLanguage: MemberLanguages | string) => {
    if (!clientFamilyBackground?.id) {
      dispatch(
        clientFamilyBackgroundActions.createClientFamilyBackground({
          treatmentPlanId: treatmentPlan?.id,
          data: {
            ...originalClientFamilyBackground,
            member_language: memberLanguage,
          },
          getAccessTokenSilently,
        }),
      );
    } else {
      dispatch(
        clientFamilyBackgroundActions.updateClientFamilyBackground({
          treatmentPlanId: treatmentPlan?.id,
          data: {
            ...originalClientFamilyBackground,
            member_language: memberLanguage,
          },
          getAccessTokenSilently,
        }),
      );
    }
  };

  const handleLanguageChange = (value: SelectValueType) => {
    const selectedLanguage = Object.values(MemberLanguages).find(
      (lang) => lang === String(value),
    );
    if (!selectedLanguage) return;

    setLanguage(selectedLanguage);

    if (selectedLanguage === MemberLanguages.Other) {
      setOtherLanguage('');
    }

    handleSaveLanguage(selectedLanguage);
  };

  const handleInputBlur = () => {
    if (!isEditable) return;

    if (isOtherLanguageValid) {
      handleSaveLanguage(otherLanguage);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      {isEditable && (
        <Typography
          color={clientAndFamilyBackgroundSectionErrorCount > 0 ? FORTA_ERROR : FORTA_BLUE.light1}
          mb={3}
          align="left"
          fontWeight={700}
        >
          {clientAndFamilyBackgroundSectionErrorCount > 0 && (
            <img
              style={{ width: '20px', height: '20px', marginRight: '8px', marginTop: '-4px' }}
              src={ErrorIcon}
              alt="attention-icon"
            />
          )}
          Attention: All fields with * are required to be filled
        </Typography>
      )}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: '24px',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: '10px',
          }}
        >
          <Typography fontSize={18} fontWeight="bold" textAlign="left">
            Member language*
          </Typography>

          {isEditable ? (
            <FHSelect
              data-testid="memberLanguageDropdown"
              sx={{ width: '210px' }}
              inputHeight={40}
              colorTheme={Themes.Primary}
              displayEmpty
              disabled={!isEditable}
              options={Object.values(MemberLanguages)
                .filter((lang) => lang !== MemberLanguages.Null)
                .map((lang) => ({
                  value: lang,
                  label: capitalizeFirstLetter(lang),
                }))}
              optionDataTestIdPrefix="memberLanguageOption"
              renderValue={
                language === MemberLanguages.Null
                  ? () => <Typography sx={{ color: FORTA_GRAY3 }}>Select language</Typography>
                  : undefined
              }
              value={language}
              onUpdate={handleLanguageChange}
              error={isEditable && language === ''}
            />
          ) : (
            <FHInputField
              sx={{ width: '210px' }}
              inputHeight={40}
              colorTheme={Themes.Primary}
              disabled={!isEditable}
              value={
                Object.values(MemberLanguages).includes(language) &&
                language === MemberLanguages.Other
                  ? capitalizeFirstLetter(otherLanguage)
                  : capitalizeFirstLetter(language)
              }
            />
          )}
        </Box>
        {language === MemberLanguages.Other && isEditable && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '10px',
            }}
          >
            <Typography fontSize={18} fontWeight="bold" textAlign="center">
              Other language*
            </Typography>

            <FHInputField
              sx={{ width: '280px' }}
              inputHeight={40}
              colorTheme={Themes.Primary}
              disabled={!isEditable}
              value={capitalizeFirstLetter(otherLanguage)}
              onChange={(e) => setOtherLanguage(e.target.value)}
              onBlur={handleInputBlur}
              error={!isOtherLanguageValid}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default MemberLanguage;
